import defaults from '/src/utils/defaults';
import { useSessionStore } from "/src/stores/session"
import { useAppStore } from "/src/stores/app"
import axios from 'axios'
import bus from '/src/utils/event_bus'


const backend_api_v2 = axios.create({
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
    baseURL: defaults.ROOT_API,
    // Boot loaders and route guards await these promises: a hung request
    // must eventually settle or the loading overlay never hides.
    timeout: 90000,
    validateStatus: function (status) {
        return status >= 200 && status < 300; // default
    },
});
// The build this app runs, sent as `X-Client-Version` so the DevOps portal can
// count the clients still on an older build. Only once /health/ says the
// backend accepts it (`accepts_client_version`): the browser refuses every
// request that carries a header the backend's CORS list leaves out, so sending
// it to an older backend would stop the whole app.
let send_client_version = false
fetch(new URL("/health/", defaults.ROOT_API), { cache: "no-store" })
    .then(response => response.json())
    .then(body => { send_client_version = body?.accepts_client_version === true })
    .catch(() => { /* no version sent: the portal counts the device without one */ })

// Auth endpoints that are allowed without authentication.
// `/my_user/password_reset/` covers the three steps of the recovery flow
// (request the token, validate it, confirm the new password), all of which run
// from the login screen before there is any session.
const AUTH_WHITELIST = [
    '/my_user/token/',
    '/my_user/token/refresh/',
    '/my_user/token/oauth/',
    '/my_user/password_reset/',
]

backend_api_v2.interceptors.request.use(config => {
    if (send_client_version) config['headers']['X-Client-Version'] = defaults.BUILD_VERSION
    let session_store
    try {
        session_store = useSessionStore()
    } catch {
        console.log("[🍍] Pinia not initialized. Sending NO_AUTH request.")
        session_store = { authenticated: false }
    }
    if (session_store.authenticated) {
        config['headers']['Authorization'] = 'Bearer ' + session_store.access_token
    } else {
        // Allow auth-related requests (login, token refresh) even when not authenticated
        const isAuthEndpoint = AUTH_WHITELIST.some(url => config.url?.includes(url))
        if (!isAuthEndpoint) {
            // Block all other requests when not authenticated
            const controller = new AbortController()
            config.signal = controller.signal
            controller.abort()
            console.log("[🔒] Blocked request (not authenticated):", config.url)
            return config
        }
        config['headers']['Authorization'] = 'NO_AUTH'
    }
    return config
})
backend_api_v2.interceptors.response.use(
    response => {
        const app_store = useAppStore()
        if (app_store.connection_lost) {
            app_store.setConnectionLost(false)
            bus.emit('notification', { timeout: 5000, text: "Connection recovered", color: "success" })
            bus.emit("back_event", { event: "connection_recovered", meta: {} })
        }
        return response;
    },
    error => {
        if (!error.response && error.code != "ERR_CANCELED") {
            console.log(error)
            const app_store = useAppStore()
            if (!app_store.connection_lost) {
                app_store.setConnectionLost(true)
                bus.emit('notification', { timeout: 5000, text: "Error - Coneection lost", color: "error" })
                bus.emit("back_event", { event: "connection_lost", meta: {} })
            }
            return Promise.reject(error);
        }
        else if (error?.response?.status == 403 && error.response?.data?.code == "token_not_valid") {
            // The access token expired. Refresh it, then send this request
            // again once: it used to fail here, and every caller had to cope
            // with an error that was only a stale token.
            const config = error.config
            if (config && !config._retried_after_refresh) {
                config._retried_after_refresh = true
                return useSessionStore().refresh()
                    .then(ok => ok ? backend_api_v2(config) : Promise.reject(error))
            }
        }
        else if (error?.response?.status == 403 && error?.response?.data?.detail == "You do not have permission to perform this action.") {
            console.log("[!] Permission error. Check the Network tab in the browser.")
            return Promise.reject(error)
        }
        else if (error?.response?.status == 403) {
            bus.emit("events/session/logout")
        }
        else if (error?.response?.status == 401 && error.response?.data?.code == "token_not_valid") {
            bus.emit("events/session/logout")
        }
        return Promise.reject(error)
    }
)


// returns a complete promise
function paginated_factory_v2(url, axiosConfig = {}) {
    function paginatedFetcher(next, obj_list = []) {
        return backend_api_v2.get(next ? next : url, axiosConfig)
            .then(({ data }) => {
                obj_list.push(...data.results)
                if (!data.next) return obj_list
                return paginatedFetcher(data.next, obj_list)
            })
    }
    return paginatedFetcher().then(data => ({ data }))
}


// API HANDLER CLASS
class ApiConnector {
    constructor() {
        this.api = backend_api_v2
        this.paginated_api = paginated_factory_v2
        this.abort = new AbortController()
    }

    get(url) {
        return this.api.get(url, { signal: this.abort.signal })
    }

    paginated_get(url) {
        return this.paginated_api(url, { signal: this.abort.signal })
    }

    post(url, data) {
        return this.api.post(url, data, { signal: this.abort.signal })
    }

    put(url, data) {
        return this.api.put(url, data, { signal: this.abort.signal })
    }

    delete(url) {
        return this.api.delete(url, { signal: this.abort.signal })
    }

    patch(url, data) {
        return this.api.patch(url, data, { signal: this.abort.signal })
    }

    cancel() {
        this.abort.abort()
        this.abort = new AbortController()
    }
}


// Rows asked for by uuid, this many per request, so the address stays well
// under any URL limit.
const UUIDS_PER_REQUEST = 100;

/** Every row of `url` with one of these uuids. */
async function fetch_by_uuid(url, uuids) {
    const out = [];
    for (let i = 0; i < uuids.length; i += UUIDS_PER_REQUEST) {
        const chunk = uuids.slice(i, i + UUIDS_PER_REQUEST);
        const { data } = await paginated_factory_v2(`${url}?uuid__in=${chunk.join(",")}`);
        out.push(...(data || []));
    }
    return out;
}


export {
    backend_api_v2,
    paginated_factory_v2, ApiConnector, fetch_by_uuid,
};


